import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import axios from "axios";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { organizationService } from "../api/organizationService";
import {
  API_UNREACHABLE_MESSAGE,
  AUTH_ORG_ORIGIN_ERROR_KEY,
  normalizeFrontendOrigin,
  shouldEnforceOrgOrigin,
} from "../lib/orgOrigin";
import type {
  AddMemberInput,
  CreateOrganizationInput,
  UpdateOrganizationInput,
  Organization,
  OrganizationMember,
  OrgRole,
} from "../types/organization";

const ACTIVE_ORG_STORAGE_KEY = "launchflow_active_org_id";

type OrgValidationErrorKind = "ORIGIN" | "FORBIDDEN" | "NETWORK";
export type OrgValidationError = { kind: OrgValidationErrorKind };

/**
 * Fetches organizations and validates that the current origin has access.
 * Throws { kind: "ORIGIN" | "FORBIDDEN" | "NETWORK" } on failure.
 * Used by the login flow to make the entire sign-in atomic.
 */
export async function validateOrgsForCurrentOrigin(): Promise<Organization[]> {
  try {
    const superAdmin = await isSuperAdminSession();
    let organizations = await organizationService.listOrganizations();

    if (!superAdmin && shouldEnforceOrgOrigin()) {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      organizations = organizations.filter((o) => {
        if (!o.frontendBaseUrl?.trim()) return false;
        return normalizeFrontendOrigin(o.frontendBaseUrl) === origin;
      });
      if (organizations.length === 0) {
        throw { kind: "ORIGIN" } as OrgValidationError;
      }
    }

    return organizations;
  } catch (err) {
    if (err && typeof err === "object" && "kind" in err) throw err;
    if (axios.isAxiosError(err) && err.response?.status === 403) {
      throw { kind: "FORBIDDEN" } as OrgValidationError;
    }
    throw { kind: "NETWORK" } as OrgValidationError;
  }
}

const SUPER_ADMINS_GROUP = "SuperAdmins";

async function isSuperAdminSession(): Promise<boolean> {
  try {
    const session = await fetchAuthSession();
    const groups =
      (session.tokens?.accessToken?.payload?.["cognito:groups"] as string[]) ||
      [];
    return groups.includes(SUPER_ADMINS_GROUP);
  } catch {
    return false;
  }
}

const loadActiveOrgFromStorage = (): string | null => {
  try {
    return localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
  } catch {
    return null;
  }
};

const saveActiveOrgToStorage = (orgId: string | null): void => {
  try {
    if (orgId) {
      localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, orgId);
    } else {
      localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
};

interface OrganizationState {
  organizations: Organization[];
  activeOrgId: string | null;
  currentUserRole: OrgRole | null;
  isRoleLoading: boolean;
  members: OrganizationMember[];
  isLoading: boolean;
  /** True while the org list / site validation (after auth) is in flight. */
  isOrgBootstrapPending: boolean;
  error: string | null;
}

interface OrganizationActions {
  setOrgBootstrapPending: (pending: boolean) => void;
  fetchOrganizations: () => Promise<void>;
  hydrateFromLogin: (organizations: Organization[]) => void;
  setActiveOrg: (orgId: string | null) => void;
  fetchCurrentUserRole: () => Promise<void>;
  fetchMembers: () => Promise<void>;
  addMember: (input: AddMemberInput) => Promise<void>;
  removeMember: (userId: string) => Promise<void>;
  updateMemberRole: (userId: string, role: OrgRole) => Promise<void>;
  createOrganization: (input: CreateOrganizationInput) => Promise<void>;
  updateOrganization: (orgId: string, input: UpdateOrganizationInput) => Promise<void>;
  deleteOrganization: (orgId: string) => Promise<void>;
  clearError: () => void;
  resetSession: () => void;
}

type OrganizationStore = OrganizationState & OrganizationActions;

export const useOrganizationStore = create<OrganizationStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      organizations: [],
      activeOrgId: loadActiveOrgFromStorage(),
      currentUserRole: null,
      isRoleLoading: false,
      members: [],
      isLoading: false,
      isOrgBootstrapPending: false,
      error: null,

      clearError: () => set({ error: null }),

      setOrgBootstrapPending: (pending: boolean) =>
        set({ isOrgBootstrapPending: pending }),

      resetSession: () => {
        saveActiveOrgToStorage(null);
        set({
          organizations: [],
          activeOrgId: null,
          currentUserRole: null,
          isRoleLoading: false,
          members: [],
          isLoading: false,
          isOrgBootstrapPending: false,
          error: null,
        });
      },

      hydrateFromLogin: (organizations: Organization[]) => {
        const storedOrgId = loadActiveOrgFromStorage();
        const validStoredOrg = organizations.some((o) => o.orgId === storedOrgId);
        const activeOrgId = validStoredOrg
          ? storedOrgId
          : organizations[0]?.orgId ?? null;
        if (activeOrgId && activeOrgId !== storedOrgId) {
          saveActiveOrgToStorage(activeOrgId);
        }
        set({
          organizations,
          activeOrgId,
          isLoading: false,
          isOrgBootstrapPending: false,
          currentUserRole: null,
          isRoleLoading: !!activeOrgId,
          members: [],
          error: null,
        });
        if (activeOrgId) {
          get().fetchCurrentUserRole();
        }
      },

      fetchOrganizations: async () => {
        try {
          set({ isLoading: true, error: null });
          const superAdmin = await isSuperAdminSession();
          let organizations = await organizationService.listOrganizations();

          if (!superAdmin && shouldEnforceOrgOrigin()) {
            const origin =
              typeof window !== "undefined" ? window.location.origin : "";
            organizations = organizations.filter((o) => {
              if (!o.frontendBaseUrl?.trim()) return false;
              return normalizeFrontendOrigin(o.frontendBaseUrl) === origin;
            });
            if (organizations.length === 0) {
              try {
                sessionStorage.setItem(
                  AUTH_ORG_ORIGIN_ERROR_KEY,
                  "Incorrect username or password",
                );
              } catch {
                /* ignore */
              }
              await signOut();
              saveActiveOrgToStorage(null);
              set({
                organizations: [],
                activeOrgId: null,
                currentUserRole: null,
                isRoleLoading: false,
                members: [],
                isLoading: false,
                error: null,
              });
              return;
            }
          }

          const storedOrgId = loadActiveOrgFromStorage();
          const validStoredOrg = organizations.some((o) => o.orgId === storedOrgId);
          const activeOrgId = validStoredOrg
            ? storedOrgId
            : organizations[0]?.orgId ?? null;
          if (activeOrgId && activeOrgId !== storedOrgId) {
            saveActiveOrgToStorage(activeOrgId);
          }
          set({
            organizations,
            activeOrgId,
            isLoading: false,
            currentUserRole: null,
            isRoleLoading: !!activeOrgId,
            members: [],
          });
          if (activeOrgId) {
            get().fetchCurrentUserRole();
          }
        } catch (err) {
          const status = axios.isAxiosError(err) ? err.response?.status : undefined;
          if (status === 403) {
            try {
              sessionStorage.setItem(
                AUTH_ORG_ORIGIN_ERROR_KEY,
                "Incorrect username or password",
              );
            } catch {
              /* ignore */
            }
            try {
              await signOut();
            } catch {
              /* ignore */
            }
            saveActiveOrgToStorage(null);
            set({
              organizations: [],
              activeOrgId: null,
              currentUserRole: null,
              isRoleLoading: false,
              members: [],
              isLoading: false,
              error: null,
            });
            return;
          }
          // Network error, 5xx, timeout, etc. — sign out as defense-in-depth for
          // sessions restored on page refresh when the backend is unreachable.
          try {
            sessionStorage.setItem(AUTH_ORG_ORIGIN_ERROR_KEY, API_UNREACHABLE_MESSAGE);
          } catch { /* ignore */ }
          try {
            await signOut();
          } catch { /* ignore */ }
          saveActiveOrgToStorage(null);
          set({
            organizations: [],
            activeOrgId: null,
            currentUserRole: null,
            isRoleLoading: false,
            members: [],
            isLoading: false,
            error: null,
          });
        } finally {
          set({ isOrgBootstrapPending: false });
        }
      },

      setActiveOrg: (orgId: string | null) => {
        saveActiveOrgToStorage(orgId);
        set({
          activeOrgId: orgId,
          currentUserRole: null,
          isRoleLoading: !!orgId,
          members: [],
        });
        if (orgId) {
          get().fetchCurrentUserRole();
        }
      },

      fetchCurrentUserRole: async () => {
        const { activeOrgId } = get();
        if (!activeOrgId) return;
        set({ isRoleLoading: true });
        try {
          const me = await organizationService.getMyOrganizationContext(activeOrgId);
          const role = me?.role ?? null;
          set((state) => ({
            currentUserRole: role,
            isRoleLoading: false,
            organizations: state.organizations.map((org) =>
              org.orgId === activeOrgId
                ? {
                    ...org,
                    isDemo: me.isDemo,
                    maxSeats: me.maxSeats,
                    maxSops: me.maxSops,
                    maxUploadBytes: me.maxUploadBytes,
                    features: me.features ?? org.features ?? {},
                    windows: me.windows ?? [],
                    remainingSops: me.remainingSops ?? null,
                    demoExpiresAt: me.demoExpiresAt ?? null,
                    demoAcceptedAt: me.demoAcceptedAt ?? null,
                  }
                : org,
            ),
          }));
        } catch {
          set({ currentUserRole: null, isRoleLoading: false });
        }
      },

      fetchMembers: async () => {
        const { activeOrgId } = get();
        if (!activeOrgId) return;
        try {
          set({ isLoading: true, error: null });
          const members = await organizationService.listMembers(activeOrgId);
          set({ members, isLoading: false });
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : "Failed to fetch members",
            isLoading: false,
          });
        }
      },

      addMember: async (input: AddMemberInput) => {
        const { activeOrgId } = get();
        if (!activeOrgId) return;
        try {
          set({ error: null });
          await organizationService.addMember(activeOrgId, input);
          await get().fetchMembers();
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : "Failed to add member",
          });
          throw err;
        }
      },

      removeMember: async (userId: string) => {
        const { activeOrgId } = get();
        if (!activeOrgId) return;
        try {
          set({ error: null });
          await organizationService.removeMember(activeOrgId, userId);
          await get().fetchMembers();
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : "Failed to remove member",
          });
          throw err;
        }
      },

      updateMemberRole: async (userId: string, role: OrgRole) => {
        const { activeOrgId } = get();
        if (!activeOrgId) return;
        try {
          set({ error: null });
          await organizationService.updateMemberRole(activeOrgId, userId, role);
          await get().fetchMembers();
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : "Failed to update role",
          });
          throw err;
        }
      },

      createOrganization: async (input: CreateOrganizationInput) => {
        try {
          set({ isLoading: true, error: null });
          const created = await organizationService.createOrganization(input);
          const organizations = await organizationService.listOrganizations();
          saveActiveOrgToStorage(created.orgId);
          set({
            organizations,
            activeOrgId: created.orgId,
            isLoading: false,
            currentUserRole: null,
            members: [],
          });
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : "Failed to create organization",
            isLoading: false,
          });
          throw err;
        }
      },

      updateOrganization: async (orgId: string, input: UpdateOrganizationInput) => {
        try {
          set({ error: null });
          const updated = await organizationService.updateOrganization(orgId, input);
          set((state) => ({
            organizations: state.organizations.map((o) =>
              o.orgId === orgId
                ? {
                    ...o,
                    name: updated.name,
                    slug: updated.slug,
                    frontendBaseUrl: updated.frontendBaseUrl,
                  }
                : o,
            ),
          }));
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : "Failed to update organization",
          });
          throw err;
        }
      },

      deleteOrganization: async (orgId: string) => {
        try {
          set({ error: null });
          await organizationService.deleteOrganization(orgId);
          const organizations = await organizationService.listOrganizations();
          const storedOrgId = loadActiveOrgFromStorage();
          const validStoredOrg = organizations.some((o) => o.orgId === storedOrgId);
          const activeOrgId = validStoredOrg
            ? storedOrgId
            : organizations[0]?.orgId ?? null;
          if (activeOrgId !== storedOrgId) {
            saveActiveOrgToStorage(activeOrgId);
          }
          set({
            organizations,
            activeOrgId,
            currentUserRole: validStoredOrg ? get().currentUserRole : null,
            members: validStoredOrg ? get().members : [],
          });
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "Failed to delete organization",
          });
          throw err;
        }
      },
    })),
    { name: "organization" },
  ),
);
